import React, { useContext, useState } from 'react';
import {
    Button,
    Space,
    Typography,
    Modal,
    Alert,
    Descriptions,
    Tag
} from 'antd';
import {
    CopyOutlined,
    ExclamationCircleOutlined,
    IdcardOutlined
} from '@ant-design/icons';
import { DarkModeContext } from "../../shared/contexts/darkModeContext";
import toast from "react-hot-toast";
import { internalApi } from "../../common/axios";
import { buildUrl } from "../../shared/utils/net";

const { Text } = Typography;

const PlayerInfoCopy = ({ leftDB, playerDataLeft, rightDB, playerDataRight }) => {
    const { darkMode } = useContext(DarkModeContext);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleCopyClick = () => {
        setConfirmModalOpen(true);
    };

    const handleConfirmCopy = async () => {
        setLoading(true);
        try {
            const url = buildUrl('player/copy', {
                userKeyLeft: playerDataLeft.userKey,
                leftDB: leftDB,
                userKeyRight: playerDataRight.userKey,
                pid: playerDataRight.pid,
                rightDB: rightDB,
            });

            const res = await internalApi.put(url);
            toast.success(res.data.message);
        } catch (err) {
            toast.error(err.response?.data?.message || '복사 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
            setConfirmModalOpen(false);
        }
    };

    return (
        <>
            {/* 복사 액션 - 컴팩트한 세로 레이아웃 */}
            <div
                className={`copy-action-container ${darkMode ? 'dark-theme' : 'light-theme'}`}
                style={{
                    background: 'var(--card-background)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                    boxShadow: 'var(--card-shadow)',
                    minWidth: '120px'
                }}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Button
                        type="primary"
                        icon={<CopyOutlined />}
                        onClick={handleCopyClick}
                        disabled={!playerDataLeft || !playerDataRight}
                        size="large"
                        block
                        style={{
                            height: '48px',
                            fontSize: '14px',
                            fontWeight: '600',
                            borderRadius: '6px'
                        }}
                    >
                        데이터 복사
                    </Button>
                    <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: '11px', lineHeight: 1.2 }}>
                            Source ➡ Target
                        </Text>
                    </div>
                </Space>
            </div>

            {/* 확인 모달 */}
            <Modal
                title={
                    <Space align="center">
                        <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '20px' }} />
                        <Text strong style={{ fontSize: '16px' }}>데이터 복사 확인</Text>
                    </Space>
                }
                open={confirmModalOpen}
                onCancel={() => setConfirmModalOpen(false)}
                className={`copy-confirm-modal ${darkMode ? 'dark-theme' : 'light-theme'}`}
                width={600}
                footer={[
                    <Button key="cancel" size="large" onClick={() => setConfirmModalOpen(false)}>
                        취소
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        size="large"
                        loading={loading}
                        onClick={handleConfirmCopy}
                    >
                        복사 실행
                    </Button>
                ]}
            >
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Alert
                        message="다음 작업을 수행하시겠습니까?"
                        type="info"
                        showIcon
                    />

                    {/* 복사 미리보기 */}
                    <div className="info-section">
                        <div className="section-header">
                            <IdcardOutlined className="section-icon" />
                            <Text strong className="section-title">복사될 플레이어 정보</Text>
                        </div>

                        <Descriptions
                            variant="outlined"
                            column={1}
                            size="small"
                            className="info-descriptions"
                        >
                            <Descriptions.Item label="닉네임">
                                <Text strong>{playerDataLeft?.playerInfo?.userInfo?.Nickname || '-'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="레벨">
                                <Tag color="blue">Lv. {playerDataLeft?.playerInfo?.userInfo?.Level || 0}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="UserKey">
                                <Text code>{playerDataLeft?.userKey}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="원본 DB">
                                <Tag color="blue">{leftDB}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="대상 DB">
                                <Tag color="orange">{rightDB}</Tag>
                            </Descriptions.Item>
                        </Descriptions>
                    </div>

                    <Alert
                        message="주의: 이 작업은 되돌릴 수 없습니다"
                        description="대상 데이터베이스의 기존 플레이어 데이터가 완전히 덮어씌워집니다."
                        type="warning"
                        showIcon
                        icon={<ExclamationCircleOutlined />}
                    />
                </Space>
            </Modal>
        </>
    );
};

export default PlayerInfoCopy;